import { useState } from 'react';
import { Tv, User, Clock, Zap, LogOut } from 'lucide-react';
import type { Seat, FamilyMember } from '@lib/streamFlowApi';

interface SeatCardProps {
  seat: Seat;
  members: FamilyMember[];
  currentMemberId: string | null;
  onClaim: (seatId: string) => void;
  onRelease: (seatId: string) => void;
}

export default function SeatCard({ seat, members, currentMemberId, onClaim, onRelease }: SeatCardProps) {
  const member = members.find(m => m.id === seat.claimed_by);
  const isOwnSeat = seat.claimed_by === currentMemberId;
  const [loading, setLoading] = useState(false);

  const timeLeft = seat.expires_at
    ? Math.max(0, Math.floor((new Date(seat.expires_at).getTime() - Date.now()) / 60000))
    : null;

  const statusColors: Record<string, string> = {
    available: 'border-green-500 bg-green-50',
    claimed: 'border-blue-500 bg-blue-50',
    checkout: 'border-amber-500 bg-amber-50',
  };

  const handleAction = async () => {
    setLoading(true);
    try {
      if (seat.status === 'available') {
        await onClaim(seat.id);
      } else if (isOwnSeat) {
        await onRelease(seat.id);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`border-2 rounded-lg p-4 ${statusColors[seat.status] || 'border-gray-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-500">Seat {seat.seat_index}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          seat.status === 'available' ? 'bg-green-100 text-green-700' :
          seat.status === 'checkout' ? 'bg-amber-100 text-amber-700' :
          'bg-blue-100 text-blue-700'
        }`}>
          {seat.status === 'checkout' ? 'Checked Out' : seat.status}
        </span>
      </div>

      {seat.status !== 'available' && member && (
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium">{member.display_name}</span>
        </div>
      )}

      {seat.current_channel_name && (
        <div className="flex items-center gap-2 mb-2">
          <Tv className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600">{seat.current_channel_name}</span>
        </div>
      )}

      {timeLeft !== null && seat.status !== 'available' && (
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-gray-400" />
          <span className="text-xs text-gray-500">{timeLeft}m remaining</span>
        </div>
      )}

      {seat.status === 'available' && currentMemberId && (
        <button
          onClick={handleAction}
          disabled={loading}
          className="w-full mt-2 px-3 py-2 bg-slate-800 text-white text-sm rounded-lg hover:bg-slate-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Zap className="w-4 h-4" />
          {loading ? 'Claiming...' : 'Claim Seat'}
        </button>
      )}

      {isOwnSeat && (
        <button
          onClick={handleAction}
          disabled={loading}
          className="w-full mt-2 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-500 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <LogOut className="w-4 h-4" />
          {loading ? 'Releasing...' : 'Release Seat'}
        </button>
      )}
    </div>
  );
}
